import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * MobileLayoutContext - Controls visibility of MobileLayout shell (header + bottom nav).
 *
 * MobileLayout provides this context. Child pages can hide/show shell elements.
 *
 * SCOPE, since this is the file callers read before touching the shell: the
 * header is ALSO route-gated inside MobileLayout, to the capture route. This
 * context can only ever hide it. A page cannot turn the header on anywhere
 * else, and `headerRight` reaches the DOM on that one route.
 * When hidden, elements are removed from DOM (not just hidden via CSS),
 * which eliminates stacking context issues entirely.
 *
 * Returns a no-op implementation when used outside MobileLayout (e.g., on desktop),
 * making it safe to call from any shared component.
 *
 * Usage:
 *   const { hideShell, showShell } = useMobileLayout();
 *   useEffect(() => { hideShell(); return () => showShell(); }, []);
 *
 *   // Or use the useImmersiveMode() convenience hook which handles this automatically.
 */

export interface ShellVisibility {
  header: boolean;
  bottomNav: boolean;
}

export interface MobileLayoutContextType {
  /** Current visibility state of shell elements */
  visibility: ShellVisibility;
  /** Hide all shell (header + bottom nav). Elements are removed from DOM. */
  hideShell: () => void;
  /** Restores the bottom nav, and the header on the capture route only. */
  showShell: () => void;
  /** Fine-grained control: set visibility of individual shell elements */
  setShellVisibility: (visibility: Partial<ShellVisibility>) => void;
  /**
   * Whether neither bar is REQUESTED. Deliberately not route-aware: off the
   * capture route the header never renders regardless, and folding that in
   * would make a page that hides only the bottom nav read as immersive —
   * which sets `overflowY: hidden` and strands the user on a scrollable page.
   */
  isImmersive: boolean;
  /**
   * Rendered on the right of the mobile header — which exists on the CAPTURE
   * ROUTE ONLY. Ignored everywhere else; put the control in the page body.
   */
  headerRight: ReactNode;
  /**
   * Set (or clear with null) the right-side header slot. SILENTLY does
   * nothing wherever the header does not render — off the capture route, and
   * on it while a page has hidden the shell. Put the control in the page body
   * instead; the two pages that used to push here now do.
   */
  setHeaderRight: (node: ReactNode) => void;
}

export const MobileLayoutContext = createContext<MobileLayoutContextType | null>(null);

export const useMobileLayout = (): MobileLayoutContextType => {
  const context = useContext(MobileLayoutContext);
  if (!context) {
    // No-op implementation for desktop or outside MobileLayout
    return {
      visibility: { header: true, bottomNav: true },
      hideShell: () => {},
      showShell: () => {},
      setShellVisibility: () => {},
      isImmersive: false,
      headerRight: null,
      setHeaderRight: () => {},
    };
  }
  return context;
};
